import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Filter, Plus, Search, Coins, MessageSquare } from "lucide-react";
import { initials, statusLabel, timeAgo, formatDate, canCreateRequest, isOwner, isDeptAdmin } from "@/lib/utils";

const isClosed = (s) => ["completed", "expired", "rejected"].includes(s);

const isClaimer = (r, me) => (r.claimers || []).some((c) => c.id === me);

const COLUMN_PILL = {
  open: "open",
  active: "in_progress",
  claimed: "claimed",
  submitted: "submitted",
  done: "done",
  pending: "pending_approval",
  review: "under_review",
  claimable: "open",
};

/** Owner org oversight pipeline */
const OWNER_COLUMNS = [
  {
    key: "open",
    label: "Open",
    match: (r) => !isClosed(r.status) && r.status !== "pending_approval" && (r.solution_count || 0) === 0,
  },
  {
    key: "active",
    label: "Active",
    match: (r) => !isClosed(r.status) && r.status !== "pending_approval" && (r.solution_count || 0) > 0,
  },
  {
    key: "pending",
    label: "Needs bounty approval",
    match: (r) => r.status === "pending_approval",
  },
  {
    key: "done",
    label: "Done",
    match: (r) => isClosed(r.status),
  },
];

/** Dept admin manage pipeline — Active / Need review merged (like My request) */
const DEPT_MANAGE_COLUMNS = [
  {
    key: "open",
    label: "Open",
    match: (r) =>
      !isClosed(r.status)
      && r.status !== "pending_approval"
      && (r.solution_count || 0) === 0
      && !(r.pending_solution_count > 0),
  },
  {
    key: "active",
    label: "Active / Need review",
    match: (r) =>
      !isClosed(r.status)
      && r.status !== "pending_approval"
      && (
        (r.solution_count || 0) > 0
        || (r.pending_solution_count || 0) > 0
      ),
  },
  {
    key: "pending",
    label: "Needs bounty approval",
    match: (r) => r.status === "pending_approval",
  },
  {
    key: "done",
    label: "Done",
    match: (r) => isClosed(r.status),
  },
];

/** Shared worker lanes: claim / solve (contributor Open tab) */
const WORKER_CLAIM_COLUMNS = [
  {
    key: "claimable",
    label: "Open to claim",
    match: (r, me) =>
      !isClaimer(r, me)
      && r.creator_id !== me
      && ["open", "claimed"].includes(r.status)
      && r.status !== "pending_approval",
  },
  {
    key: "claimed",
    label: "Claimed by me",
    match: (r, me) =>
      isClaimer(r, me)
      && !isClosed(r.status)
      && (!r.my_solution || ["pending", "changes_requested", "withdrawn"].includes(r.my_solution?.status)),
  },
  {
    key: "submitted",
    label: "Submitted",
    match: (r, me) =>
      !isClosed(r.status)
      && !!r.my_solution
      && ["approved", "rejected"].includes(r.my_solution.status),
  },
];

/** Contributor Open tab: claim lanes + done (excludes own creations via list filter) */
const CONTRIBUTOR_OPEN_COLUMNS = [
  ...WORKER_CLAIM_COLUMNS,
  {
    key: "done",
    label: "Done",
    match: (r, me) =>
      isClosed(r.status) && (isClaimer(r, me) || !!r.my_solution),
  },
];

/** My request: creator pipeline (contributor, dept admin, owner) */
const MY_REQUEST_COLUMNS = [
  {
    key: "open",
    label: "Open",
    match: (r) =>
      !isClosed(r.status)
      && r.status !== "pending_approval"
      && (r.solution_count || 0) === 0
      && !(r.pending_solution_count > 0),
  },
  {
    key: "active",
    label: "Active / Need review",
    match: (r) =>
      !isClosed(r.status)
      && (
        r.status === "pending_approval"
        || (r.solution_count || 0) > 0
        || (r.pending_solution_count || 0) > 0
      ),
  },
  {
    key: "done",
    label: "Done",
    match: (r) => isClosed(r.status),
  },
];

function groupByColumns(requests, columns, userId) {
  const m = Object.fromEntries(columns.map((c) => [c.key, []]));
  const assigned = new Set();
  for (const r of requests) {
    const col = columns.find((c) => {
      if (assigned.has(r.id)) return false;
      return c.match.length >= 2 ? c.match(r, userId) : c.match(r);
    });
    if (col) {
      m[col.key].push(r);
      assigned.add(r.id);
    }
  }
  return m;
}

export default function Board() {
  const { user } = useAuth();
  const owner = isOwner(user);
  const deptAdmin = isDeptAdmin(user);
  const contributor = !owner && !deptAdmin;
  const [requests, setRequests] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [cat, setCat] = useState(params.get("category") || "all");
  const [dept, setDept] = useState(params.get("department") || "all");
  const tabParam = params.get("tab");
  const [deptTab, setDeptTab] = useState(tabParam === "my-request" ? "my-request" : "manage");
  const [ownerTab, setOwnerTab] = useState(tabParam === "my-request" ? "my-request" : "manage");
  const [contributorTab, setContributorTab] = useState(tabParam === "my-request" ? "my-request" : "open");
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);

  const load = async () => {
    const p = {};
    if (q) p.q = q;
    if (cat !== "all") p.category = cat;
    // Owner Manage may filter any dept; My request loads all so own tickets aren't hidden
    if (owner && ownerTab === "manage" && dept !== "all") p.department = dept;
    const { data } = await api.get("/requests", { params: p });
    setRequests(data);
  };

  useEffect(() => {
    api.get("/org/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/org/departments").then((r) => setDepartments(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (cat !== "all") next.category = cat;
    if (owner && ownerTab === "manage" && dept !== "all") next.department = dept;
    if (owner && ownerTab !== "manage") next.tab = ownerTab;
    if (deptAdmin && deptTab !== "manage") next.tab = deptTab;
    if (contributor && contributorTab !== "open") next.tab = contributorTab;
    setParams(next, { replace: true });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, cat, dept, deptTab, ownerTab, contributorTab, owner, deptAdmin, contributor]);

  const filtered = useMemo(() => {
    let list = requests;

    if (deptAdmin) {
      const home = user?.department;
      list = list.filter(
        (r) =>
          r.department === home
          || r.creator?.department === home
          || r.creator_id === user?.id
          || isClaimer(r, user?.id)
          || r.visibility === "org"
          || !r.visibility
          || (r.visibility === "specific" && (r.allowed_user_ids || []).includes(user?.id)),
      );
    }

    return list;
  }, [requests, deptAdmin, user?.id, user?.department]);

  const manageList = useMemo(() => {
    if (!deptAdmin) return filtered;
    const home = user?.department;
    return filtered.filter(
      (r) =>
        r.department === home
        || r.creator?.department === home
        || r.visibility === "org"
        || !r.visibility
        || (r.visibility === "specific" && (r.allowed_user_ids || []).includes(user?.id)),
    );
  }, [filtered, deptAdmin, user?.department]);

  const myRequestList = useMemo(() => {
    if (!(contributor || deptAdmin || owner)) return [];
    return requests.filter((r) => r.creator_id === user?.id);
  }, [requests, contributor, deptAdmin, owner, user?.id]);

  const openSectionList = useMemo(() => {
    if (!contributor) return [];
    return filtered.filter((r) => r.creator_id !== user?.id);
  }, [filtered, contributor, user?.id]);

  const pendingBountyCount = (deptAdmin ? manageList : filtered).filter(
    (r) => r.status === "pending_approval",
  ).length;

  const myRequestReviewCount = myRequestList.filter(
    (r) =>
      !isClosed(r.status)
      && (
        r.status === "pending_approval"
        || (r.pending_solution_count || 0) > 0
        || (r.solution_count || 0) > 0
      ),
  ).length;

  const subtitle = owner
    ? "Manage the org pipeline, or track requests you created."
    : deptAdmin
      ? "Manage your department, or track requests you created."
      : "Claim and solve open asks, or track requests you created.";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-600">Request Board</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-2">Pipeline</h1>
          <p className="text-slate-600 mt-1">{subtitle}</p>
        </div>
        {canCreateRequest(user) && (
          <Button asChild data-testid="board-create-btn" className="bg-blue-600 hover:bg-blue-700">
            <Link to="/app/requests/new"><Plus className="h-4 w-4 mr-1" /> New request</Link>
          </Button>
        )}
      </div>

      {owner && (
        <div className="flex rounded-lg border border-slate-200 overflow-hidden w-fit text-sm" data-testid="owner-board-tabs">
          <button
            type="button"
            className={`px-4 py-2 ${ownerTab === "manage" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
            onClick={() => setOwnerTab("manage")}
          >
            Manage
            {pendingBountyCount > 0 && ownerTab !== "manage" && (
              <span className="ml-2 font-mono text-[10px] bg-orange-500 text-white rounded-full px-1.5">{pendingBountyCount}</span>
            )}
          </button>
          <button
            type="button"
            className={`px-4 py-2 ${ownerTab === "my-request" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
            onClick={() => setOwnerTab("my-request")}
          >
            My request
            {myRequestReviewCount > 0 && ownerTab !== "my-request" && (
              <span className="ml-2 font-mono text-[10px] bg-orange-500 text-white rounded-full px-1.5">{myRequestReviewCount}</span>
            )}
          </button>
        </div>
      )}

      {deptAdmin && (
        <div className="flex rounded-lg border border-slate-200 overflow-hidden w-fit text-sm" data-testid="dept-board-tabs">
          <button
            type="button"
            className={`px-4 py-2 ${deptTab === "manage" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
            onClick={() => setDeptTab("manage")}
          >
            Manage
            {pendingBountyCount > 0 && deptTab !== "manage" && (
              <span className="ml-2 font-mono text-[10px] bg-orange-500 text-white rounded-full px-1.5">{pendingBountyCount}</span>
            )}
          </button>
          <button
            type="button"
            className={`px-4 py-2 ${deptTab === "my-request" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
            onClick={() => setDeptTab("my-request")}
          >
            My request
            {myRequestReviewCount > 0 && deptTab !== "my-request" && (
              <span className="ml-2 font-mono text-[10px] bg-orange-500 text-white rounded-full px-1.5">{myRequestReviewCount}</span>
            )}
          </button>
        </div>
      )}

      {contributor && (
        <div className="flex rounded-lg border border-slate-200 overflow-hidden w-fit text-sm" data-testid="contributor-board-tabs">
          <button
            type="button"
            className={`px-4 py-2 ${contributorTab === "open" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
            onClick={() => setContributorTab("open")}
          >
            Open
          </button>
          <button
            type="button"
            className={`px-4 py-2 ${contributorTab === "my-request" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}
            onClick={() => setContributorTab("my-request")}
          >
            My request
            {myRequestReviewCount > 0 && contributorTab !== "my-request" && (
              <span className="ml-2 font-mono text-[10px] bg-orange-500 text-white rounded-full px-1.5">{myRequestReviewCount}</span>
            )}
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search title, description, tags…"
            data-testid="board-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="board-category-filter" className="w-48">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {owner && ownerTab === "manage" && (
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger data-testid="board-dept-filter" className="w-44">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {departments.map((d) => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {deptAdmin && deptTab === "manage" && user?.department && (
          <Badge variant="outline" className="text-xs font-mono" data-testid="board-dept-locked">
            {user.department}
          </Badge>
        )}
        <div className="font-mono text-xs text-slate-500 ml-auto flex items-center gap-2">
          {pendingBountyCount > 0 && (
            (owner && ownerTab === "manage") || (deptAdmin && deptTab === "manage")
          ) && (
            <span className="pill pill-pending_approval">{pendingBountyCount} pending</span>
          )}
        </div>
      </div>

      {owner && ownerTab === "manage" && (
        <PipelineBoard columns={OWNER_COLUMNS} requests={filtered} />
      )}

      {owner && ownerTab === "my-request" && (
        <PipelineBoard columns={MY_REQUEST_COLUMNS} requests={myRequestList} />
      )}

      {deptAdmin && deptTab === "manage" && (
        <PipelineBoard columns={DEPT_MANAGE_COLUMNS} requests={manageList} />
      )}

      {deptAdmin && deptTab === "my-request" && (
        <PipelineBoard columns={MY_REQUEST_COLUMNS} requests={myRequestList} />
      )}

      {contributor && contributorTab === "open" && (
        <WorkerBoard columns={CONTRIBUTOR_OPEN_COLUMNS} requests={openSectionList} userId={user?.id} />
      )}

      {contributor && contributorTab === "my-request" && (
        <PipelineBoard columns={MY_REQUEST_COLUMNS} requests={myRequestList} />
      )}
    </div>
  );
}

function PipelineBoard({ columns, requests }) {
  const grouped = useMemo(() => groupByColumns(requests, columns), [requests, columns]);
  return (
    <BoardColumns
      columns={columns}
      grouped={grouped}
      renderCard={(r) => <PipelineCard key={r.id} r={r} />}
    />
  );
}

function WorkerBoard({ columns, requests, userId }) {
  const grouped = useMemo(
    () => groupByColumns(requests, columns, userId),
    [requests, columns, userId],
  );
  return (
    <BoardColumns
      columns={columns}
      grouped={grouped}
      renderCard={(r) => <WorkerCard key={r.id} r={r} />}
    />
  );
}

function PipelineCard({ r }) {
  const isRejected = r.status === "rejected";
  return (
    <CardShell r={r} className={isRejected ? "border-rose-300 bg-rose-50/40" : undefined}>
      {isRejected && (
        <div className="mt-3 rounded-lg border border-rose-200 bg-white px-2.5 py-2 text-[11px] text-rose-800 leading-snug" data-testid={`card-rejection-${r.id}`}>
          <div className="font-semibold mb-0.5">Rejected</div>
          {r.approval_feedback ? (
            <span className="line-clamp-3 whitespace-pre-wrap">{r.approval_feedback}</span>
          ) : (
            <span className="text-rose-600">Bounty request was rejected.</span>
          )}
        </div>
      )}
      {!isRejected && (
        <>
          <div className="flex items-center justify-between mt-4">
            <div className="flex -space-x-2">
              {r.creator && (
                <Avatar className="h-6 w-6 border-2 border-white">
                  <AvatarImage src={r.creator.avatar_url || ""} />
                  <AvatarFallback className="text-[9px]">{initials(r.creator.name)}</AvatarFallback>
                </Avatar>
              )}
              {(r.claimers || []).slice(0, 3).map((c) => (
                <Avatar key={c.id} className="h-6 w-6 border-2 border-white">
                  <AvatarImage src={c.avatar_url || ""} />
                  <AvatarFallback className="text-[9px]">{initials(c.name)}</AvatarFallback>
                </Avatar>
              ))}
            </div>
            <span className="font-mono text-xs font-bold text-blue-600">{r.bounty_credits}c</span>
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[11px] font-mono text-slate-500">
              <MessageSquare className="h-3.5 w-3.5" /> {r.solution_count || 0} solution{(r.solution_count || 0) === 1 ? "" : "s"}
            </span>
            {(r.pending_solution_count || 0) > 0 && (
              <span className="text-[10px] font-mono font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                {r.pending_solution_count} pending
              </span>
            )}
          </div>
        </>
      )}
    </CardShell>
  );
}

function WorkerCard({ r }) {
  const solStatus = r.my_solution?.status;
  return (
    <CardShell r={r}>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        {solStatus && (
          <Badge
            variant={solStatus === "approved" ? "default" : solStatus === "rejected" ? "destructive" : "outline"}
            className="text-[10px]"
          >
            {solStatus.replace("_", " ")}
          </Badge>
        )}
        {(r.pending_solution_count || 0) > 0 && r.creator_id && (
          <span className="text-[10px] font-mono font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
            {r.pending_solution_count} to review
          </span>
        )}
        {(r.claimers?.length || 0) > 0 && (
          <span className="text-[10px] font-mono text-slate-500">{r.claimers.length} claimed</span>
        )}
      </div>
      <div className="flex items-center justify-between mt-4">
        <span className="inline-flex items-center gap-1 font-mono text-xs font-bold text-blue-600">
          <Coins className="h-3.5 w-3.5 text-amber-500" /> {r.bounty_credits}c
        </span>
        {r.due_date && (
          <span className="text-[10px] text-slate-400 font-mono">due {formatDate(r.due_date)}</span>
        )}
      </div>
    </CardShell>
  );
}

function BoardColumns({ columns, grouped, renderCard }) {
  return (
    <div className="kanban-scroll flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
      {columns.map((col) => (
        <div key={col.key} className="flex-shrink-0 w-[320px]" data-testid={`column-${col.key}`}>
          <div className="flex items-center justify-between mb-3 px-1">
            <span className={`pill pill-${COLUMN_PILL[col.key] || "open"}`}>{col.label}</span>
            <span className="text-xs font-mono text-slate-500">{grouped[col.key]?.length || 0}</span>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 min-h-[60vh] space-y-3">
            {(grouped[col.key] || []).map(renderCard)}
            {(grouped[col.key] || []).length === 0 && (
              <div className="text-xs text-slate-400 text-center py-6">No items</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function CardShell({ r, children, className }) {
  return (
    <Link
      to={`/app/requests/${r.id}`}
      data-testid={`card-request-${r.id}`}
      className={`block bg-white rounded-xl border border-slate-200 p-4 card-hover ${className || ""}`}
    >
      <div className="flex items-center justify-between mb-2">
        <Badge variant="outline" className="text-[10px]">{r.category}</Badge>
        <span className={`font-mono text-xs font-semibold diff-${r.difficulty}`}>{r.difficulty?.toUpperCase()}</span>
      </div>
      <div className="font-semibold text-sm leading-snug line-clamp-2">{r.title}</div>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <span className={`pill pill-${r.status}`}>{statusLabel(r.status)}</span>
        {r.department && (
          <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{r.department}</span>
        )}
        <span className="text-[10px] text-slate-400 font-mono">{timeAgo(r.created_at)}</span>
      </div>
      {r.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {r.tags.slice(0, 3).map((t) => (
            <span key={t} className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">#{t}</span>
          ))}
        </div>
      )}
      {children}
    </Link>
  );
}
